import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";

const submitSchema = z.object({
  name: z.string().trim().min(2).max(120),
  publication: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(40).optional().nullable(),
  website_url: z.string().trim().max(300).optional().nullable(),
  request_type: z.string().trim().min(1).max(80),
  message: z.string().max(2000).optional().nullable(),
  website: z.string().optional().nullable(), // honeypot
  source: z.string().max(80).optional().nullable(),
  utm_source: z.string().max(120).optional().nullable(),
  utm_medium: z.string().max(120).optional().nullable(),
  utm_campaign: z.string().max(160).optional().nullable(),
  utm_content: z.string().max(160).optional().nullable(),
  referrer: z.string().max(500).optional().nullable(),
  landing_page: z.string().max(500).optional().nullable(),
});

async function sha256(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export const submitMediaRequest = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => submitSchema.parse(data))
  .handler(async ({ data }) => {
    // Honeypot: silently accept, don't persist or email.
    if (data.website && data.website.trim().length > 0) {
      return { ok: true } as const;
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const tenMinAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    const { count: recentCount } = await supabaseAdmin
      .from("media_requests")
      .select("id", { count: "exact", head: true })
      .eq("email", data.email.toLowerCase())
      .gte("created_at", tenMinAgo);
    if ((recentCount ?? 0) >= 3) {
      return { ok: false, error: "Too many requests. Please try again later." } as const;
    }

    const request = getRequest();
    const ip =
      request?.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      request?.headers.get("cf-connecting-ip") ||
      "";
    const ipHash = ip ? await sha256(ip) : null;

    const { data: inserted, error } = await supabaseAdmin
      .from("media_requests")
      .insert({
        name: data.name,
        publication: data.publication,
        email: data.email.toLowerCase(),
        phone: data.phone ?? null,
        website_url: data.website_url ?? null,
        request_type: data.request_type,
        message: data.message ?? null,
        ip_hash: ipHash,
        source: data.utm_source || data.source || "media_center",
        utm_source: data.utm_source ?? null,
        utm_medium: data.utm_medium ?? null,
        utm_campaign: data.utm_campaign ?? null,
        utm_content: data.utm_content ?? null,
        referrer: data.referrer ?? null,
        landing_page: data.landing_page ?? null,
      })
      .select("id")
      .single();

    if (error || !inserted) {
      console.error("[media] insert failed", error);
      return { ok: false, error: "Could not save request. Please try again." } as const;
    }

    const { sendEmail } = await import("./emails/send");
    const { mediaAdminNotificationEmail, mediaAckEmail } = await import("./emails/templates");

    const ackTpl = mediaAckEmail({ name: data.name });
    const ackRes = await sendEmail({ to: data.email, subject: ackTpl.subject, html: ackTpl.html });

    const adminEmail = process.env.ADMIN_EMAIL;
    let adminRes: { ok: boolean; error?: string } = { ok: false, error: "ADMIN_EMAIL not set" };
    if (adminEmail) {
      const adminTpl = mediaAdminNotificationEmail({
        name: data.name,
        publication: data.publication,
        email: data.email,
        phone: data.phone,
        websiteUrl: data.website_url,
        requestType: data.request_type,
        message: data.message,
        source: data.utm_source || data.source || "media_center",
        leadId: inserted.id,
      });
      adminRes = await sendEmail({
        to: adminEmail,
        subject: adminTpl.subject,
        html: adminTpl.html,
        reply_to: data.email,
      });
    } else {
      console.warn("[media] ADMIN_EMAIL not configured");
    }

    if (adminRes.ok) {
      await supabaseAdmin
        .from("media_requests")
        .update({ admin_notified: true, admin_notified_at: new Date().toISOString() })
        .eq("id", inserted.id);
    }

    return { ok: true, emailSent: ackRes.ok, adminNotified: adminRes.ok } as const;
  });
